/**
 * photoQuality.js — resuelve el PERFIL DE CALIDAD de la captura de fotos de ocurrencias.
 *
 * La organización activa la llave `photo_full_quality` en el blob `extra_settings` de
 * `general_settings`. Ese blob ya se pide al abrir una ocurrencia (`Occurrence.vue:177`) y en el
 * listado previo (`Forms.vue:38`), y queda en el store de organización — no hace falta ningún request
 * nuevo. Llave AUSENTE = todo sigue igual.
 *
 * Este módulo NO lee el store: recibe `extraSettings` de quien sí tiene acceso legítimo (el getter
 * `getExtraSettings` mapeado en los mixins de foto). El último valor conocido se cachea junto con la
 * organización del usuario autenticado, para que un device compartido no reutilice el flag de otro tenant.
 *
 * QUÉ HACE EL FLAG (medido durante el análisis del perfil de calidad):
 *   - web                  -> sin cambios.
 *   - móvil > 3 GB de RAM  -> techo de 2400 px, calidad 92 y UNA sola generación JPEG (sin reencode
 *                             en el WebView, sin el recorte agresivo del blob > 2 MB).
 *   - móvil <= 3 GB de RAM -> perfil `high` (1500 px / q90 / 0.85), que es lo que corría en campo
 *                             antes de la configuración por tier de junio-2026.
 *
 * El PERFIL DE CALIDAD es independiente del TIER DE MEMORIA: `getDeviceTier()`, `skipPhotoEditor` y
 * `shouldElevatePriority()` siguen decidiendo por RAM real y no se tocan desde aquí. Un handheld de
 * 2 GB con el flag activo captura con parámetros de `high` pero sigue siendo tier `low` para todo lo
 * que protege de OOM.
 */

// Capacitor
import { Capacitor } from '@capacitor/core'

// Utils
import { getDeviceInfo, getDeviceTier, TIER_CONFIG } from '@/utils/deviceCapability'

export const PHOTO_FULL_QUALITY_KEY = 'photo_full_quality'
export const PHOTO_FULL_QUALITY_CACHE_KEY = 'photo_full_quality_cache'

// RAM (GB) desde la que se aplica el perfil de calidad completa. `navigator.deviceMemory` redondea a
// potencias de 2, así que un equipo de 3 GB reporta 2 y cae en el perfil `high`; uno de 4 GB reporta 4.
export const FULL_QUALITY_MIN_RAM_GB = 4

// Sin Device Memory API (iOS, escritorio) se asume gama alta, igual que `getDeviceTier()`.
export const ASSUMED_RAM_GB = 8

export const DEFAULT_EDITOR_QUALITY = 0.85
export const FULL_QUALITY_REENCODE = 0.92

// Heurística de respaldo cuando no se pueden leer dimensiones: una foto que supera este peso puede
// indicar que la cámara ignoró width/height. El techo real se decide por dimensiones JPEG cuando
// están disponibles; este valor solo cubre formatos no JPEG o encabezados no analizables.
export const FULL_QUALITY_CEILING_BYTES = 6_000_000

/** Guarda el valor conocido junto con la organización a la que pertenece. */
export function cachePhotoFullQuality (orgId, value) {
  try {
    if (orgId == null || value === undefined) localStorage.removeItem(PHOTO_FULL_QUALITY_CACHE_KEY)
    else localStorage.setItem(PHOTO_FULL_QUALITY_CACHE_KEY, JSON.stringify({ org: String(orgId), value: !!value }))
  } catch { /* sin localStorage: no-op */ }
}

/** Solo devuelve el cache si pertenece a la organización activa. */
export function readCachedPhotoFullQuality (orgId) {
  try {
    const raw = JSON.parse(localStorage.getItem(PHOTO_FULL_QUALITY_CACHE_KEY) || 'null')
    if (!raw || orgId == null || raw.org !== String(orgId)) return undefined
    return raw.value
  } catch { return undefined }
}

export const FULL_QUALITY_PROFILE = {
  quality: FULL_QUALITY_REENCODE * 100,
  targetWidth: 2400,
  targetHeight: 2400,
  compressQuality: FULL_QUALITY_REENCODE,
  editorQuality: FULL_QUALITY_REENCODE,
  passthrough: true,
  largeBlobFallback: false,
  ceilingBytes: FULL_QUALITY_CEILING_BYTES
}

/** Perfil equivalente al comportamiento actual para un tier dado. */
function profileFromTier (tier) {
  const config = TIER_CONFIG[tier] || TIER_CONFIG.high

  return {
    quality: config.quality,
    targetWidth: config.targetWidth,
    targetHeight: config.targetHeight,
    compressQuality: config.compressQuality,
    editorQuality: DEFAULT_EDITOR_QUALITY,
    passthrough: false,
    largeBlobFallback: true,
    ceilingBytes: null
  }
}

/**
 * Lee la llave del blob de extra_settings.
 * @returns {boolean|undefined} undefined = la llave no viene = la organización no opina.
 */
export function parsePhotoFullQuality (raw) {
  let blob = raw

  if (typeof blob === 'string') {
    try {
      blob = JSON.parse(blob)
    } catch {
      return undefined
    }
  }

  const settings = Array.isArray(blob) ? blob[0] : blob
  if (!settings || typeof settings !== 'object') return undefined

  const value = settings[PHOTO_FULL_QUALITY_KEY]

  if (value === true || value === 'true') return true
  if (value === false || value === 'false') return false

  return undefined
}

/** @param {*} extraSettings el blob `extra_settings` del store de organización. */
export function isPhotoFullQualityEnabled (extraSettings) {
  return parsePhotoFullQuality(extraSettings) === true
}

/**
 * Perfil de calidad efectivo para la captura.
 * @param {{ enabled?: boolean, extraSettings?: *, organizationId?: number|string, native?: boolean, ram?: number|null }} overrides
 *        costura para tests.
 */
export function getPhotoQualityProfile (overrides = {}) {
  const tierProfile = profileFromTier(getDeviceTier())

  const fromStore = parsePhotoFullQuality(overrides.extraSettings)
  const enabled = overrides.enabled !== undefined
    ? overrides.enabled
    : fromStore ?? (readCachedPhotoFullQuality(overrides.organizationId) === true)
  if (!enabled) return tierProfile

  const native = overrides.native !== undefined ? overrides.native : Capacitor.isNativePlatform()
  if (!native) return tierProfile

  const ram = (overrides.ram !== undefined ? overrides.ram : getDeviceInfo().ram) ?? ASSUMED_RAM_GB

  return ram >= FULL_QUALITY_MIN_RAM_GB ? { ...FULL_QUALITY_PROFILE } : profileFromTier('high')
}
